<div class="head">
  <h2 class="mat-h2">Review queue</h2>
  <mat-form-field appearance="outline">
    <mat-label>Producer</mat-label>
    <mat-select [(ngModel)]="producerFilter" (ngModelChange)="filterChanged()">
      <mat-option value="">All</mat-option>
      <mat-option *ngFor="let producer of producers" [value]="producer">{{ producer }}</mat-option>
    </mat-select>
  </mat-form-field>
  <span class="grow"></span>
  <span class="counter" *ngIf="items.length">{{ index + 1 }} of {{ items.length }}</span>
  <span class="counter muted" *ngIf="settledAtZero">
    · {{ settledAtZero }} broadcast{{ settledAtZero === 1 ? '' : 's' }} left out, already at zero
  </span>
</div>

<mat-card class="card" *ngIf="item; else empty">
  <mat-card-header>
    <mat-card-title>{{ item.def.label }}</mat-card-title>
    <mat-card-subtitle>
      <a [routerLink]="['/evaluation/game', item.game.videoId]">{{ item.game.name }}</a>
      · {{ item.game.producerName }} · {{ item.game.date }}
      <span *ngIf="item.at !== null"> · first seen {{ time(item.at) }}</span>
    </mat-card-subtitle>
  </mat-card-header>

  <mat-card-content>
    <div class="why">
      <b>What the tool found:</b> {{ item.note || 'nothing it would commit to' }}
    </div>

    <div class="swing" *ngIf="swing">
      This ruling is worth <b>{{ swing | number }} CZK</b> to the producer.
    </div>
    <div class="swing muted" *ngIf="!swing">
      This one does not change the payment — it is measured for the record.
    </div>

    <!-- Commentary and ambient sound are settled by listening, so the sample
         sits above the facts rather than under them: it is the first thing the
         reviewer wants and it answers in ten seconds. -->
    <app-evidence-audio *ngIf="audioClip"
                        [path]="audioClip.storagePath"
                        [caption]="audioCaption">
    </app-evidence-audio>

    <table class="facts" *ngIf="facts().length">
      <tbody>
        <tr *ngFor="let fact of facts()">
          <td class="muted">{{ fact.label }}</td>
          <td>{{ fact.value }}</td>
        </tr>
      </tbody>
    </table>

    <!-- The recording itself, dropped at the second in question. Only the
         machine that downloaded the broadcasts serves it, so elsewhere the
         player simply is not there. -->
    <app-recording-player [videoId]="item.game.videoId" [at]="item.at"
                          [localFile]="isAdmin ? item.game.localFile : null">
    </app-recording-player>

    <div class="evidence" *ngIf="item.evidence.length">
      <app-evidence-image *ngFor="let event of item.evidence"
                          [path]="event.storagePath"
                          [caption]="(event.slot || event.kind) + ' · ' + time(event.start)">
      </app-evidence-image>
    </div>

    <div class="links">
      <a mat-stroked-button [href]="item.game.dashboardUrl" target="_blank" rel="noopener">
        <mat-icon>open_in_new</mat-icon>
        Watch on StayLive
      </a>
      <!-- Where the recording sits on the machine that analysed it. Clicking
           copies rather than opens: Chrome blocks file:// from a web page and
           does not say so. -->
      <a mat-stroked-button *ngIf="isAdmin && item.game.localFile"
         href="file://{{ item.game.localFile }}"
         (click)="copyLocalFile($event)"
         matTooltip="Path on the machine that analysed the game. Click to copy — browsers refuse to open file:// from a web page.">
        <mat-icon>movie</mat-icon>
        {{ copiedPath ? 'Path copied' : 'Recording on disk' }}
      </a>
    </div>

    <mat-form-field appearance="outline" class="reason">
      <mat-label>Reason (kept with the ruling)</mat-label>
      <input matInput [(ngModel)]="note" placeholder="e.g. rain delay, the recording covers the whole game">
    </mat-form-field>
  </mat-card-content>

  <mat-card-actions class="actions">
    <button mat-button [disabled]="index === 0" (click)="previous()">
      <mat-icon>chevron_left</mat-icon>
      Previous
    </button>
    <!-- A criterion that is a matter of degree is dialled, not answered: the
         admin sets what it costs the production fee. "Not met" there would be
         the far end of a range pretending to be an answer. -->
    <ng-container *ngIf="maximumCutPercent !== null; else plainRuling">
      <mat-form-field appearance="outline" class="cut">
        <mat-label>Production cut %</mat-label>
        <input matInput type="number" min="0" step="5" [max]="maximumCutPercent" [(ngModel)]="cut">
        <mat-hint>0 – {{ maximumCutPercent }} %, nothing else in the payment changes</mat-hint>
      </mat-form-field>
      <button mat-flat-button color="primary" [disabled]="busy" (click)="applyCut()">Apply cut</button>
    </ng-container>
    <ng-template #plainRuling>
      <button mat-flat-button color="primary" [disabled]="busy" (click)="decide(1)">Met</button>
      <button mat-stroked-button [disabled]="busy" (click)="decide(0)">Not met</button>
    </ng-template>
    <button mat-button [disabled]="index >= items.length - 1" (click)="next()">
      Skip
      <mat-icon>chevron_right</mat-icon>
    </button>
  </mat-card-actions>
</mat-card>

<ng-template #empty>
  <mat-card class="card">
    <mat-card-content>
      <p>Nothing waiting for a human{{ producerFilter ? ' at ' + producerFilter : '' }}.</p>
      <p class="muted" *ngIf="settledAtZero">
        {{ settledAtZero }} broadcast{{ settledAtZero === 1 ? ' is' : 's are' }} not listed: an absolute
        condition failed, so nothing left to rule on would change the payment. They can be released as they are.
      </p>
      <a mat-stroked-button routerLink="/evaluation/season">Back to the season</a>
    </mat-card-content>
  </mat-card>
</ng-template>
